import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, getDocs, query } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from "../components/ui/dialog";
import { 
  ArrowLeft, Search, MapPin, Send, Home, Clock, CheckCircle2, X
} from 'lucide-react';
import { toast } from 'sonner';

// IMPORTAÇÃO DA NAVBAR (OPÇÃO B)
import Navbar from '../components/ui/Navbar';

import planoFundo from "../assets/planodefundo.png"; 

const FindTechnician = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, authLoading } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [editLocation, setEditLocation] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dbFunctions, setDbFunctions] = useState([]);
  const [searchArea, setSearchArea] = useState(location.state?.selectedArea || '');

  const [formData, setFormData] = useState({
    serviceArea: location.state?.selectedArea || '',
    description: '',
    cep: '',
    address: '',
    neighborhood: '',
    city: '',
    state: '',
    number: '',
    complement: '',
    propertyType: 'casa',
    urgency: 'normal'
  });

  // Busca as especialidades reais do banco
  useEffect(() => {
    const fetchFunctions = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'functions')));
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDbFunctions(data.filter(f => f.active !== false).sort((a, b) => a.name.localeCompare(b.name)));
      } catch (e) { console.error(e); }
    };
    fetchFunctions();
  }, []);

  const filteredAreas = useMemo(() => {
    return dbFunctions.filter(f => 
      f.name.toLowerCase().includes(searchArea.toLowerCase())
    );
  }, [searchArea, dbFunctions]);

  useEffect(() => {
    if (userProfile?.addressDetails) {
      const addr = userProfile.addressDetails;
      setFormData(prev => ({
        ...prev,
        cep: addr.cep || '',
        address: addr.street || '',
        neighborhood: addr.neighborhood || '',
        city: addr.city || '',
        state: addr.state || '',
        number: addr.number || '',
        complement: addr.complement || ''
      }));
    }
  }, [userProfile]);

  const handleCepBlur = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (data.erro) return toast.error('CEP não encontrado.');
      setFormData(prev => ({
        ...prev,
        address: data.logradouro,
        neighborhood: data.bairro,
        city: data.localidade,
        state: data.uf
      }));
    } catch (error) { toast.error('Erro ao buscar CEP.'); }
  };

  const generateServiceCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

  const handleFinalSubmit = async () => {
    if (!formData.serviceArea) return toast.error("Selecione uma especialidade da lista!");
    setLoading(true);
    const serviceCode = generateServiceCode();
    const fullLocationString = `${formData.address}, ${formData.number}${formData.complement ? ` (${formData.complement})` : ''}${formData.neighborhood ? ` - ${formData.neighborhood}` : ''}, ${formData.city}/${formData.state}`;

    try {
      await addDoc(collection(db, 'orders'), {
        address: fullLocationString,
        clientId: user.uid,
        clientName: userProfile?.name || 'Cliente',
        clientPhoto: userProfile?.photoURL || '',
        createdAt: new Date().toISOString(),
        description: formData.description,
        propertyType: formData.propertyType,
        serviceArea: formData.serviceArea,
        serviceCode: serviceCode,
        status: 'open',
        technicianId: null,
        updatedAt: new Date().toISOString(),
        urgency: formData.urgency,
        addressDetails: { ...formData }
      });
      setShowConfirmModal(false);
      toast.success(`Pedido publicado! Código: ${serviceCode}`, { duration: 10000 });
      navigate('/dashboard');
    } catch (error) { toast.error('Erro ao publicar pedido.'); }
    finally { setLoading(false); }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  if (!user || !userProfile) return null;

  return (
    <div className="relative min-h-screen font-sans text-slate-900 overflow-x-hidden">
      <div className="fixed inset-0 -z-10 w-full h-full transform-gpu" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}><div className="absolute inset-0 bg-slate-900/40" /></div>

      {/* NAVBAR COMPONENTIZADA */}
      <Navbar />

      <main className="relative z-10 max-w-4xl mx-auto px-6 pt-32 pb-12">
        
        {/* BOTÃO VOLTAR RÁPIDO */}
        <div className="mb-6">
           <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full font-black text-[10px] uppercase tracking-tighter italic text-white hover:bg-white/10">
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar para o início
           </Button>
        </div>

        <div className="bg-white/95 rounded-[2.5rem] shadow-2xl border border-white/50 overflow-hidden transform-gpu animate-in fade-in zoom-in-95 duration-500">
          
          <div className="bg-blue-700 p-8 md:p-12 text-white">
             <h2 className="text-3xl font-black uppercase italic tracking-tighter leading-none mb-2 text-white">ABRIR CHAMADO</h2>
             <p className="text-blue-100 text-[10px] font-black italic opacity-80 uppercase tracking-[0.2em]">
               Seu pedido será enviado para profissionais em <span className="text-orange-400 underline decoration-2 underline-offset-4 uppercase">{formData.city || "sua região"}</span>
             </p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); setShowConfirmModal(true); }} className="p-8 md:p-12 space-y-10">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* ESPECIALIDADE */}
              <div className="relative space-y-3">
                <Label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 ml-2 italic">Especialidade</Label>
                <div className="relative group">
                  <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input 
                    placeholder="O que você precisa?" 
                    className="h-16 pl-16 pr-12 rounded-[1.5rem] bg-slate-50/50 font-black uppercase italic border-slate-200"
                    value={searchArea}
                    onChange={(e) => { setSearchArea(e.target.value); setIsDropdownOpen(true); }}
                    onFocus={() => setIsDropdownOpen(true)}
                  />
                  {searchArea && <button type="button" onClick={() => {setSearchArea(''); setFormData({...formData, serviceArea: ''});}} className="absolute right-5 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 rounded-full"><X className="w-4 h-4 text-slate-400" /></button>}
                </div>
                {isDropdownOpen && filteredAreas.length > 0 && (
                  <div className="absolute top-[100%] left-0 right-0 z-[200] mt-2 bg-white border border-slate-200 shadow-2xl rounded-2xl overflow-hidden animate-in fade-in">
                    <div className="max-h-[250px] overflow-y-auto custom-scrollbar">
                      {filteredAreas.map(f => (
                        <div key={f.id} className="px-6 py-4 font-black uppercase italic text-[11px] text-slate-600 hover:bg-blue-600 hover:text-white cursor-pointer border-b border-slate-50 last:border-none"
                          onClick={() => { setSearchArea(f.name); setFormData({...formData, serviceArea: f.name}); setIsDropdownOpen(false); }}>
                          {f.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* TIPO DE IMÓVEL */}
              <div className="space-y-3">
                <Label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 ml-2 italic">Tipo de Imóvel</Label>
                <Select value={formData.propertyType} onValueChange={(v) => setFormData({...formData, propertyType: v})}>
                  <SelectTrigger className="h-16 rounded-[1.5rem] bg-slate-50/50 font-black uppercase italic border-slate-200 px-6">
                    <div className="flex items-center gap-3"><Home className="w-4 h-4 text-blue-600" /><SelectValue /></div>
                  </SelectTrigger>
                  <SelectContent className="bg-white shadow-2xl rounded-2xl z-[200]">
                    <SelectItem value="casa" className="font-bold uppercase italic text-[11px] py-4">Casa</SelectItem>
                    <SelectItem value="apartamento" className="font-bold uppercase italic text-[11px] py-4">Apartamento</SelectItem>
                    <SelectItem value="comercial" className="font-bold uppercase italic text-[11px] py-4">Comercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* DESCRIÇÃO */}
            <div className="space-y-3">
              <Label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 ml-2 italic">Explique o problema</Label>
              <Textarea placeholder="Detalhe o que você precisa que seja feito..." className="min-h-[160px] rounded-[1.8rem] border-slate-200 bg-slate-50/50 font-bold p-8 text-slate-700" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
            </div>

            {/* URGÊNCIA */}
            <div className="space-y-3">
              <Label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 ml-2 italic">Qual a urgência?</Label>
              <Select value={formData.urgency} onValueChange={(v) => setFormData({...formData, urgency: v})}>
                <SelectTrigger className="h-16 rounded-[1.5rem] bg-slate-50/50 font-black uppercase italic border-slate-200 px-6">
                  <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-orange-500" /><SelectValue /></div>
                </SelectTrigger>
                <SelectContent className="bg-white shadow-2xl rounded-2xl z-[200]">
                  <SelectItem value="low" className="font-bold uppercase italic text-[11px] py-4">Baixa - Pode esperar</SelectItem>
                  <SelectItem value="normal" className="font-bold uppercase italic text-[11px] py-4">Normal - Próximos dias</SelectItem>
                  <SelectItem value="high" className="font-bold uppercase italic text-[11px] py-4">Alta - Urgente</SelectItem>
                  <SelectItem value="emergency" className="font-bold uppercase italic text-[11px] py-4 text-red-600 font-black">Emergência</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* LOCALIZAÇÃO */}
            <div className="pt-4 border-t border-slate-100">
               <div className="flex justify-between items-center mb-4 ml-2">
                <Label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 italic">Endereço do Chamado</Label>
                <button type="button" onClick={() => setEditLocation(!editLocation)} className="text-[10px] font-black text-blue-600 uppercase italic hover:underline">{editLocation ? "Travar" : "Alterar"}</button>
              </div>
              {!editLocation ? (
                <div className="p-7 bg-blue-50/40 rounded-[1.8rem] border border-dashed border-blue-200 flex items-center gap-5">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm"><MapPin className="w-6 h-6 text-red-500" /></div>
                  <div className="flex flex-col">
                    <span className="font-bold text-slate-700 italic text-sm leading-tight">
                      {formData.address}, {formData.number} {formData.complement && `- ${formData.complement}`} <br /> 
                      {formData.neighborhood} - {formData.city}/{formData.state}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-in slide-in-from-top-4">
                  <Input placeholder="CEP" className="h-16 rounded-2xl font-bold bg-white border-slate-200" value={formData.cep} onChange={(e) => setFormData({...formData, cep: e.target.value})} onBlur={handleCepBlur} required />
                  <Input placeholder="Número" className="h-16 rounded-2xl font-bold bg-white border-slate-200" value={formData.number} onChange={(e) => setFormData({...formData, number: e.target.value})} required />
                  <div className="md:col-span-2">
                     <Input placeholder="Logradouro" disabled className="h-16 rounded-2xl font-bold bg-slate-50 opacity-70 border-slate-200" value={formData.address} />
                  </div>
                  <Input placeholder="Bairro" disabled className="h-16 rounded-2xl font-bold bg-slate-50 opacity-70 border-slate-200" value={formData.neighborhood} />
                  <Input placeholder="Cidade" disabled className="h-16 rounded-2xl font-bold bg-slate-50 opacity-70 border-slate-200" value={formData.city} />
                  <div className="md:col-span-2">
                    <Input placeholder="Complemento (Apto, Bloco...)" className="h-16 rounded-2xl font-bold bg-white border-slate-200" value={formData.complement} onChange={(e) => setFormData({...formData, complement: e.target.value})} />
                  </div>
                </div>
              )}
            </div>

            <Button type="submit" className="w-full h-20 bg-orange-500 hover:bg-orange-600 text-white rounded-[1.8rem] font-black uppercase italic shadow-2xl flex items-center justify-center gap-4 transition-all active:scale-95 shadow-orange-900/20">
              <Send className="w-5 h-5" /> REVISAR E PUBLICAR AGORA
            </Button>
          </form>
        </div>
      </main>

      {/* MODAL DE CONFIRMAÇÃO */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="bg-white rounded-[2.5rem] border-none shadow-2xl max-w-md p-10 z-[300]">
          <DialogHeader className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner"><CheckCircle2 className="w-10 h-10 text-blue-600" /></div>
            <DialogTitle className="text-2xl font-black uppercase italic text-slate-900 tracking-tighter leading-none">Confirmar Envio?</DialogTitle>
            <DialogDescription className="font-bold italic text-slate-500 mt-2">Sua solicitação será enviada para profissionais de {formData.city}.</DialogDescription>
          </DialogHeader>

          <div className="bg-slate-50/80 rounded-3xl p-7 my-8 space-y-4 border border-slate-100 text-left">
            <div className="flex justify-between items-center"><span className="text-[10px] font-black uppercase text-slate-400">Serviço:</span> <span className="font-black italic text-blue-700 uppercase text-xs">{formData.serviceArea}</span></div>
            <div className="flex justify-between items-center"><span className="text-[10px] font-black uppercase text-slate-400">Urgência:</span> <span className="font-black italic text-orange-500 uppercase text-xs">{formData.urgency}</span></div>
            <div className="border-t border-slate-200 pt-4"><p className="text-[11px] font-bold text-slate-600 leading-relaxed">{formData.address}, {formData.number} {formData.complement && `- ${formData.complement}`} - {formData.city}</p></div>
          </div>

          <DialogFooter className="flex flex-col gap-4">
            <Button onClick={handleFinalSubmit} disabled={loading} className="w-full h-16 bg-blue-700 hover:bg-blue-800 text-white rounded-2xl font-black uppercase italic tracking-widest text-xs shadow-xl shadow-blue-900/20">
              {loading ? "PUBLICANDO..." : "CONFIRMAR E PUBLICAR"}
            </Button>
            <Button variant="ghost" onClick={() => setShowConfirmModal(false)} className="w-full font-black uppercase italic text-slate-400 text-[10px] hover:text-red-500 transition-colors">Voltar e ajustar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; } * { -webkit-tap-highlight-color: transparent; }` }} />
    </div>
  );
};

export default FindTechnician;